import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoTimeOutline,
  IoLogOutOutline,
  IoSwapHorizontalOutline,
  IoWarningOutline,
  IoArrowForward,
  IoCubeOutline,
  IoPencil,
  IoSunny,
  IoMoon,
  IoPhonePortrait,
  IoSearchOutline,
} from "react-icons/io5";
import { toast } from "sonner";
import api from "@/api";
import { useI18n, ALL_STORES, STORE_LABELS } from "@/i18n";
import { useAuth } from "@/auth";
import { useTheme } from "@/theme";
import { relativeTime, STORE_IMAGES, isFeminineName } from "@/utils";
import { LogoutDialog } from "@/components/LogoutDialog";
import { ThemeDialog } from "@/components/ThemeDialog";
import { NotificationButton } from "@/components/NotificationButton";
import { BottomNav } from "@/components/BottomNav";
import { StoreGridSkeleton } from "@/components/Skeletons";
import { PullToRefresh } from "@/components/PullToRefresh";
import { compressImage } from "@/utils/image";
import { tapLight } from "@/utils/haptics";

export default function DashboardPage() {
  const { t, lang } = useI18n();
  const { user, logout, isTitolare, isDirettore } = useAuth();
  const navigate = useNavigate();
  // Seed from localStorage cache for instant paint (stale-while-revalidate)
  const cacheKey = `tf_dash_cache_${user?.name || "anon"}_${isTitolare ? "T" : "D"}`;
  const cached = (() => {
    try { return JSON.parse(localStorage.getItem(cacheKey) || "null"); } catch { return null; }
  })();
  const [summaries, setSummaries] = useState(cached?.summaries || {});
  const [totalProblems, setTotalProblems] = useState(cached?.totalProblems || 0);
  const [totalExpiries, setTotalExpiries] = useState(cached?.totalExpiries || 0);
  const [lastUpdated, setLastUpdated] = useState(cached?.lastUpdated || {});
  const [storeMeta, setStoreMeta] = useState(cached?.storeMeta || {});
  const [pendingIncoming, setPendingIncoming] = useState(cached?.pendingIncoming || 0);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [dashLoading, setDashLoading] = useState(!cached);
  const { theme, effective } = useTheme();
  const uploadingRef = useRef({});

  const load = useCallback(async () => {
    try {
      const summaryPromise = Promise.all(ALL_STORES.map((s) => api.get(`/stores/${s}/summary`).then((r) => [s, r.data])));
      const storesPromise = Promise.all(ALL_STORES.map((s) => api.get(`/stores/${s}`).then((r) => [s, r.data]).catch(() => [s, {}])));
      // History is titolare-only (403 for direttore)
      const historyPromise = isTitolare
        ? Promise.all(ALL_STORES.map((s) => api.get("/history", { params: { store_id: s, limit: 1 } }).then((r) => [s, r.data[0]?.created_at]).catch(() => [s, null])))
        : Promise.resolve(ALL_STORES.map((s) => [s, null]));
      // Incoming reservations (only for direttore)
      const reservationsPromise = isDirettore
        ? api.get("/reservations", { params: { role_filter: "incoming", status: "pending" } }).then((r) => r.data.length).catch(() => 0)
        : Promise.resolve(0);

      const [summariesR, historyR, storesR, incoming] = await Promise.all([summaryPromise, historyPromise, storesPromise, reservationsPromise]);
      const map = Object.fromEntries(summariesR);
      const totProblems = summariesR.reduce((sum, [, v]) => sum + (v.open_problems || 0), 0);
      const totExpiries = summariesR.reduce((sum, [, v]) => sum + (v.expiries_today_count || 0), 0);
      const lu = Object.fromEntries(historyR);
      const sm = Object.fromEntries(storesR);
      setSummaries(map);
      setTotalProblems(totProblems);
      setTotalExpiries(totExpiries);
      setLastUpdated(lu);
      setStoreMeta(sm);
      setPendingIncoming(incoming);
      // Persist to localStorage for next mount
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          summaries: map,
          totalProblems: totProblems,
          totalExpiries: totExpiries,
          lastUpdated: lu,
          storeMeta: sm,
          pendingIncoming: incoming,
          ts: Date.now(),
        }));
      } catch { /* quota */ }
    } catch (err) {
      console.error("Dashboard load failed:", err);
    } finally {
      setDashLoading(false);
    }
  }, [isTitolare, isDirettore, cacheKey]);

  useEffect(() => { load(); }, [load]);

  const problemsLabel =
    totalProblems === 0
      ? t("no_problems_dash")
      : totalProblems === 1
      ? t("problems_count_1")
      : t("problems_count_n").replace("$", totalProblems);

  const storeImage = (id) => {
    const meta = storeMeta[id];
    if (meta?.image_file_id) {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("tf_token");
      return `${API}/api/files/${meta.image_file_id}?auth=${encodeURIComponent(token || "")}`;
    }
    return meta?.image_url || STORE_IMAGES[id];
  };

  const uploadStoreImage = async (storeId, rawFile) => {
    if (!rawFile) return;
    uploadingRef.current[storeId] = true;
    try {
      const file = await compressImage(rawFile, { maxSide: 1600, quality: 0.82 });
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/stores/${storeId}/image`, null, { params: { image_file_id: data.id, image_url: "" } });
      toast.success("Immagine aggiornata");
      load();
    } catch {
      toast.error("Errore upload");
    } finally {
      uploadingRef.current[storeId] = false;
    }
  };

  // Dynamic font size for welcome text based on name length
  const nameLen = (user?.name || "").length;
  const welcomeFontSize = useMemo(() => {
    if (nameLen <= 6) return 30;
    if (nameLen <= 10) return 26;
    if (nameLen <= 14) return 22;
    if (nameLen <= 20) return 18;
    return 16;
  }, [nameLen]);

  return (
    <div className="mobile-frame with-bottom-bar" data-testid="dashboard-page">
      <PullToRefresh onRefresh={load}>
      <div className="tf-hero-header">
        <div style={{ display: "flex", gap: 8 }}>
          {isDirettore && (
            <button
              data-testid="header-reservations-btn"
              className="tf-icon-btn yellow"
              onClick={() => navigate("/reservations")}
              aria-label={t("reservations")}
              style={{ position: "relative" }}
            >
              <IoSwapHorizontalOutline size={20} />
              {pendingIncoming > 0 && (
                <span className="tf-badge-dot" data-testid="reservations-badge">{pendingIncoming}</span>
              )}
            </button>
          )}
          {isTitolare && (
            <button
              data-testid="header-history-btn"
              className="tf-icon-btn yellow"
              onClick={() => navigate("/history")}
              aria-label={t("history")}
            >
              <IoTimeOutline size={20} />
            </button>
          )}
          <button
            data-testid="theme-btn"
            className="tf-icon-btn yellow"
            onClick={() => setThemeOpen(true)}
            aria-label={t("theme")}
          >
            {theme === "auto" ? <IoPhonePortrait size={20} /> : effective === "dark" ? <IoMoon size={20} /> : <IoSunny size={20} />}
          </button>
        </div>
        <div className="tf-hero-brand" data-testid="hero-brand">{t("app_name")}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <NotificationButton />
          <button
            data-testid="logout-btn"
            className="tf-icon-btn yellow"
            onClick={() => setLogoutOpen(true)}
            aria-label={t("logout")}
          >
            <IoLogOutOutline size={20} />
          </button>
        </div>
      </div>

      <div className="tf-welcome-wrap">
        <div
          className="tf-welcome-line"
          data-testid="welcome-title"
          style={{ fontSize: welcomeFontSize }}
        >
          {isFeminineName(user?.name) ? t("welcome_f") : t("welcome")} <span className="name-red">{user?.name}</span>
        </div>
      </div>
      <div className="tf-yellow-underline" />

      <div className="tf-content" style={{ paddingTop: 0 }}>
        <div className="tf-stack">
          <button
            data-testid="dash-problems-btn"
            className="tf-alert-card red"
            onClick={() => navigate("/problems")}
          >
            <span className="tf-alert-icon-box">
              <IoWarningOutline size={30} />
            </span>
            <span className="tf-alert-body">
              <div className="tf-alert-title">{t("problems")}</div>
              <div className="tf-alert-sub">{problemsLabel}</div>
            </span>
            <span className="tf-alert-arrow">
              <IoArrowForward size={20} />
            </span>
          </button>

          <button
            data-testid="dash-expiries-btn"
            className="tf-alert-card black"
            onClick={() => navigate("/expiries")}
          >
            <span className="tf-alert-icon-box">
              <IoTimeOutline size={30} />
            </span>
            <span className="tf-alert-body">
              <div className="tf-alert-title">{t("expiries")}</div>
              <div className="tf-alert-sub">
                {totalExpiries === 0
                  ? (lang === "it" ? "Nessun nuovo prodotto oggi" : "No new items today")
                  : totalExpiries === 1
                  ? (lang === "it" ? "1 nuovo prodotto oggi" : "1 new item today")
                  : (lang === "it" ? `${totalExpiries} nuovi prodotti oggi` : `${totalExpiries} new items today`)}
              </div>
            </span>
            <span className="tf-alert-arrow">
              <IoArrowForward size={20} />
            </span>
          </button>
        </div>

        <div className="tf-section-divider">
          <span className="dot" />
          {t("stores_short")}
          <span className="dot" />
        </div>

        <button
          type="button"
          data-testid="dashboard-search-bar"
          className="tf-dashboard-search-bar"
          onClick={() => { tapLight(); navigate("/search"); }}
        >
          <IoSearchOutline size={18} className="tf-search-icon" />
          <span className="tf-dashboard-search-placeholder">
            {t("search_everywhere_placeholder")}
          </span>
        </button>

        <div className="tf-stores-grid">
          {dashLoading && Object.keys(summaries).length === 0 ? (
            <StoreGridSkeleton count={4} />
          ) : ALL_STORES.map((store) => {
            const s = summaries[store] || {};
            const count = s.products_count ?? 0;
            return (
              <div key={store} style={{ position: "relative" }}>
                <label
                  data-testid={`store-edit-${store}`}
                  className="tf-store-edit"
                  aria-label={t("change_image")}
                  title={t("change_image")}
                >
                  <IoPencil size={16} />
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      uploadStoreImage(store, e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </label>
                <button
                  data-testid={`store-card-${store}`}
                  className="tf-store-card"
                  onClick={() => { tapLight(); navigate(`/catalog/${store}`); }}
                >
                  <div className="tf-store-image">
                    <img
                      src={storeImage(store)}
                      alt={t(STORE_LABELS[store])}
                      loading="eager"
                      fetchpriority="high"
                      decoding="async"
                    />
                  </div>
                  <div className="tf-store-body">
                    <div className="tf-store-header-row">
                      <div className="tf-store-name">{t(STORE_LABELS[store])}</div>
                      <div className="tf-store-count-pill" data-testid={`store-count-${store}`}>
                        <IoCubeOutline size={12} />
                        <span>{count}</span>
                      </div>
                    </div>
                    <span className="tf-store-enter">
                      {t("enter")}
                      <IoArrowForward size={16} />
                    </span>
                  </div>
                </button>
              </div>
            );
          })}
        </div>

        <div className="tf-section-divider">
          <span className="dot" />
          LISTINO CONAD
          <span className="dot" />
        </div>

        <button
          type="button"
          data-testid="dashboard-listino-bar"
          className="tf-dashboard-search-bar"
          onClick={() => { tapLight(); navigate("/listino"); }}
        >
          <IoSearchOutline size={18} className="tf-search-icon" />
          <span className="tf-dashboard-search-placeholder">
            Cerca nel listino
          </span>
        </button>

        {isTitolare && (
          <button
            type="button"
            data-testid="dashboard-listini-manager-btn"
            onClick={() => { tapLight(); navigate("/listini"); }}
            style={{
              marginTop: 10,
              width: "100%",
              background: "transparent",
              color: "#0a0a0a",
              border: "1.5px solid #0a0a0a",
              borderRadius: 12,
              padding: "12px 14px",
              fontWeight: 700,
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              cursor: "pointer",
            }}
          >
            Gestisci listini Conad
            <IoArrowForward size={16} />
          </button>
        )}
      </div>
      </PullToRefresh>
      <LogoutDialog open={logoutOpen} onOpenChange={setLogoutOpen} onConfirm={logout} />
      <ThemeDialog open={themeOpen} onOpenChange={setThemeOpen} />
      <BottomNav />
    </div>
  );
}
